import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { signInGoogle } from '@/api/auth';
import { readReferralCode } from '@/utils/referral';
import styled from 'styled-components';

declare global {
   interface Window {
      google?: {
         accounts: {
            id: {
               initialize: (config: any) => void;
               renderButton: (element: HTMLElement, config: any) => void;
            };
         };
      };
   }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

interface AuthSuccess {
   token: string;
   user: {
      id?: string;
      name: string;
      email: string;
      verified?: boolean;
      role?: string;
   };
}

interface Props {
   /** Caller decides what to do post-auth: navigate, accept a transfer,
    *  etc. Receives the same shape the standard signin endpoint returns
    *  so the caller can hand off to authStore.setAuth themselves. */
   onSuccess: (result: AuthSuccess) => void;
   text?: 'continue_with' | 'signup_with' | 'signin_with';
}

/**
 * Headless Google sign-in button — handles SDK load, Google account
 * picker, and the server auth-token mint, but DELEGATES the post-auth
 * UX (setAuth + navigation) to the caller. Use this when sign-in is a
 * step inside a larger flow (e.g. device-transfer handoff) instead of
 * the standalone /login page where the original `GoogleSignInButton`
 * already does setAuth + navigate.
 */
const GoogleAuthButton: React.FC<Props> = ({ onSuccess, text = 'signup_with' }) => {
   const buttonRef = useRef<HTMLDivElement>(null);
   const containerRef = useRef<HTMLDivElement>(null);
   const [sdkLoaded, setSdkLoaded] = useState(false);

   const { mutate } = useMutation({
      mutationFn: signInGoogle,
      onSuccess: (response) => {
         const { token, user } = response.data;
         if (
            user &&
            typeof user === 'object' &&
            typeof user.name === 'string' &&
            typeof user.email === 'string'
         ) {
            onSuccess({ token, user });
         } else {
            toast.error('Google sign-in returned invalid user data');
         }
      },
      onError: (err: any) => {
         const errorMessage =
            err?.response?.data?.err ||
            err?.response?.data?.message ||
            'Google sign-in failed. Please try again.';
         toast.error(errorMessage);
      },
   });

   const handleGoogleCallback = (response: any) => {
      if (response.credential) {
         mutate({
            idToken: response.credential,
            referralCode: readReferralCode(),
         });
      } else {
         toast.error('No credential received from Google');
      }
   };

   useEffect(() => {
      const existingScript = document.getElementById('google-gsi-script');
      if (existingScript) {
         setSdkLoaded(true);
         return;
      }
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => setSdkLoaded(true);
      document.head.appendChild(script);
   }, []);

   // Render + re-render Google's button on container resize. The SDK
   // takes width as a fixed integer in pixels (and caps it at 400),
   // so we have to redraw whenever the container size changes — e.g.
   // window resize, or the parent card switching between mobile and
   // desktop layouts. Without this, the button stays at its original
   // pixel width and either overflows narrow screens or floats inside
   // a wide card.
   useEffect(() => {
      if (!sdkLoaded || !window.google) return;
      window.google.accounts.id.initialize({
         client_id: GOOGLE_CLIENT_ID,
         callback: handleGoogleCallback,
      });

      const draw = () => {
         if (!buttonRef.current || !containerRef.current || !window.google) return;
         // Google clamps to 400px max regardless of what we pass —
         // pre-clamp here so the math is honest. Floor to integer.
         const available = Math.max(
            240,
            Math.floor(containerRef.current.offsetWidth)
         );
         const width = Math.min(400, available);
         buttonRef.current.innerHTML = '';
         window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            width,
            text,
         });
      };

      draw();

      const ro = new ResizeObserver(draw);
      if (containerRef.current) ro.observe(containerRef.current);
      return () => ro.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [sdkLoaded, text]);

   return (
      <Wrapper ref={containerRef}>
         <ButtonHost ref={buttonRef} />
      </Wrapper>
   );
};

export default GoogleAuthButton;

const Wrapper = styled.div`
   width: 100%;
   display: flex;
   justify-content: center;
`;

const ButtonHost = styled.div`
   width: 100%;
   max-width: 400px;
   /* Google's iframe wrapper sometimes inlines a smaller width than we
    * asked for — pin to 100% of this host so the visible button always
    * spans the host. */
   & > div {
      width: 100% !important;
   }
   & iframe {
      width: 100% !important;
   }
`;
